import { Tabs as BaseTabs } from '@base-ui/react/tabs';
import { RiAddLine, RiCloseLine } from '@remixicon/react';
import { Fragment } from 'react';
import { cn } from '@/shared/lib/cn';
import { renderIcon, type IconLike } from './icon';

/* ================================================================================================
 * Tabs — panel tabs of side sheets (532:12209 "Panel tab'lari"): h40 row with a White/10 bottom line;
 * tabs share the width, padding 12/0/10, gap 6; label 500 12/auto White/60, active 600 white with a 2px `#35b5ec`
 * underline; count chip h14, padding 1/6, r8, White/10, 500 10/auto White/60.
 * ============================================================================================== */

export interface TabItem<T extends string> {
  value: T;
  label: React.ReactNode;
  count?: number | string;
  icon?: IconLike;
  disabled?: boolean;
}

export interface TabsProps<T extends string> {
  items: readonly TabItem<T>[];
  value?: T;
  defaultValue?: T;
  onValueChange?: (value: T) => void;
  /** Tabs share the full width (default) or hug their content. */
  fullWidth?: boolean;
  /** `<TabsPanel>` elements (optional — tabs can also drive external content). */
  children?: React.ReactNode;
  'aria-label'?: string;
  className?: string;
  listClassName?: string;
}

export function Tabs<T extends string>({
  items,
  value,
  defaultValue,
  onValueChange,
  fullWidth = true,
  children,
  'aria-label': ariaLabel,
  className,
  listClassName,
}: TabsProps<T>) {
  return (
    <BaseTabs.Root
      value={value}
      defaultValue={defaultValue ?? items[0]?.value}
      onValueChange={(next) => onValueChange?.(next as T)}
      className={cn('flex min-h-0 flex-col', className)}
    >
      <BaseTabs.List aria-label={ariaLabel} className={cn('flex h-40 shrink-0 items-stretch border-b border-line', listClassName)}>
        {items.map((item) => (
          <BaseTabs.Tab
            key={item.value}
            value={item.value}
            disabled={item.disabled}
            className={cn(
              'relative flex min-w-0 cursor-pointer items-center justify-center gap-6 border-b-2 border-transparent px-12 pt-12 pb-10',
              'text-xs leading-auto font-medium tracking-normal text-fg-secondary outline-none transition-colors duration-150',
              'hover:text-fg focus-visible:bg-hover data-active:border-accent-2 data-active:font-semibold data-active:text-fg',
              'data-disabled:cursor-not-allowed data-disabled:opacity-40',
              fullWidth && 'flex-1',
            )}
          >
            {renderIcon(item.icon, 'size-14 shrink-0')}
            <span className="truncate">{item.label}</span>
            {item.count != null && (
              <span className="inline-flex h-14 shrink-0 items-center rounded-8 bg-field px-6 text-10 leading-auto font-medium text-fg-secondary tabular-nums">
                {item.count}
              </span>
            )}
          </BaseTabs.Tab>
        ))}
      </BaseTabs.List>
      {children}
    </BaseTabs.Root>
  );
}

export function TabsPanel({ value, className, children, keepMounted }: { value: string; className?: string; children: React.ReactNode; keepMounted?: boolean }) {
  return (
    <BaseTabs.Panel value={value} keepMounted={keepMounted} className={cn('min-h-0 outline-none', className)}>
      {children}
    </BaseTabs.Panel>
  );
}

/* ================================================================================================
 * SubTabs — "Papkalar paneli" sub-tab bar (441:26581, 378:6276, 501:10789): h48, padding 0/16/0/26, gap 20,
 * `bg-panel` + bottom line; label 15/auto (active 600 white, idle 400 White/60); count badge h15 padding 1/6 r10
 * 600 11/auto (active accent + white, idle White/10 + white — 578:14120); 1×20 White/10 dividers; "+ Qo'shish".
 * ============================================================================================== */

export interface SubTabItem {
  id: string;
  label: React.ReactNode;
  count?: number | string;
  icon?: IconLike;
  /** Trailing close-line 16 (441:26581). Requires `onClose`. */
  closable?: boolean;
  disabled?: boolean;
}

export interface SubTabsProps {
  items: readonly SubTabItem[];
  activeId: string;
  onSelect: (id: string) => void;
  onClose?: (id: string) => void;
  /** "+ Qo'shish" action after the tabs. */
  action?: { label: React.ReactNode; icon?: IconLike; onClick: () => void; disabled?: boolean };
  /** Content pinned to the right end of the bar. */
  aside?: React.ReactNode;
  closeLabel?: string;
  'aria-label'?: string;
  className?: string;
}

function Divider() {
  return <span aria-hidden className="h-20 w-px shrink-0 bg-line" />;
}

export function SubTabs({
  items,
  activeId,
  onSelect,
  onClose,
  action,
  aside,
  closeLabel = 'Yopish',
  'aria-label': ariaLabel,
  className,
}: SubTabsProps) {
  return (
    <BaseTabs.Root value={activeId} onValueChange={(next) => onSelect(String(next))} className={className}>
      <div className="flex h-48 items-center gap-20 border-b border-line bg-panel pr-16 pl-26">
        <BaseTabs.List aria-label={ariaLabel} className="flex min-w-0 items-center gap-20 overflow-x-auto">
          {items.map((item, i) => (
            <Fragment key={item.id}>
              {i > 0 && <Divider />}
              <span className="group/subtab flex shrink-0 items-center gap-8">
                <BaseTabs.Tab
                  value={item.id}
                  disabled={item.disabled}
                  onKeyDown={(e) => {
                    if (item.closable && onClose && (e.key === 'Delete' || e.key === 'Backspace')) {
                      e.preventDefault();
                      onClose(item.id);
                    }
                  }}
                  className={cn(
                    'flex cursor-pointer items-center gap-8 rounded-4 text-15 leading-auto font-normal whitespace-nowrap text-fg-secondary outline-none',
                    'transition-colors duration-150 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-accent-2',
                    'data-active:font-semibold data-active:text-fg data-disabled:cursor-not-allowed data-disabled:opacity-40',
                  )}
                >
                  {renderIcon(item.icon, 'size-16 shrink-0')}
                  <span>{item.label}</span>
                  {item.count != null && (
                    <span
                      className={cn(
                        'inline-flex h-15 items-center rounded-10 px-6 text-11 leading-auto font-semibold tabular-nums',
                        item.id === activeId ? 'bg-accent text-on-accent' : 'bg-field text-fg',
                      )}
                    >
                      {item.count}
                    </span>
                  )}
                </BaseTabs.Tab>
                {item.closable && onClose && (
                  <button
                    type="button"
                    tabIndex={-1}
                    aria-label={closeLabel}
                    onClick={() => onClose(item.id)}
                    className="flex size-16 cursor-pointer items-center justify-center rounded-4 text-icon hover:bg-hover hover:text-fg"
                  >
                    <RiCloseLine aria-hidden className="size-16" />
                  </button>
                )}
              </span>
            </Fragment>
          ))}
        </BaseTabs.List>
        {action && (
          <>
            {items.length > 0 && <Divider />}
            <button
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className="flex shrink-0 cursor-pointer items-center gap-8 rounded-4 text-15 leading-auto whitespace-nowrap text-fg-secondary transition-colors duration-150 hover:text-fg disabled:cursor-not-allowed disabled:opacity-40"
            >
              {renderIcon(action.icon ?? RiAddLine, 'size-16 shrink-0 text-icon')}
              {action.label}
            </button>
          </>
        )}
        {aside && <div className="ml-auto flex shrink-0 items-center gap-10">{aside}</div>}
      </div>
    </BaseTabs.Root>
  );
}
